import { Button, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { Status } from '@/features/bidding/constants'
import { useSaveBidsMutation } from '@/features/bidding/hooks/useSaveBidsMutation'
import type { ExtendedBid } from '@/features/bidding/types/bid'

type CreateBidsButtonProps = {
  bids: ExtendedBid[]
  disabled?: boolean
  onSuccess?: () => void
}

function CreateBidsButton({ bids, disabled = false, onSuccess }: Readonly<CreateBidsButtonProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { saveBids, isPending } = useSaveBidsMutation()

  const handleClick = async () => {
    try {
      const bidsWithOfferedStatus = bids.map((bid) => ({ ...bid, status: Status.OFFERED }))
      await saveBids(bidsWithOfferedStatus)
      pushAlert({
        message: t('bidding.bidding_cascading.create_bids.success.text', { count: bids.length }),
        severity: 'success',
        title: t('bidding.bidding_cascading.create_bids.success.title'),
      })
      onSuccess?.()
    } catch {
      // Error is already handled by useSaveBidsMutation onError callback
    }
  }

  const isDisabled = disabled || bids.length === 0 || isPending

  return (
    <Button data-testid="create-bids-button" disabled={isDisabled} variant="contained" onClick={handleClick}>
      {isPending ? (
        <CircularProgress color="inherit" size={20} />
      ) : (
        t('bidding.bidding_cascading.create_bids.button', { count: bids.length })
      )}
    </Button>
  )
}

export default CreateBidsButton
